import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import {
  normalizeCreatorOneTimeEarning,
  normalizeTrafficSourceCost,
  serializeCreatorCosts,
  serializeOneTimePayment,
} from '@/lib/adapters/creator-costs.adapter'
import { request } from '@/lib/services'
import type {
  CreatorOneTimeEarning,
  CreatorOneTimeEarningRaw,
  CreatorOneTimePaymentForm,
  CreatorTrafficSourceCost,
  CreatorTrafficSourceCostRaw,
} from '@/lib/types/creator-costs.types'
import { useAdvertiserStore } from '@/stores/advertiser.stores'

export const useCreatorCosts = () => {
  const legacyStore = useStore()
  const advertiserStore = useAdvertiserStore()
  const isSaving = ref(false)
  const error = ref<Error | null>(null)

  const trafficSources = computed<CreatorTrafficSourceCost[]>(() => {
    const raw: CreatorTrafficSourceCostRaw[] =
      legacyStore.getters['influencer/influencer']?.trafficSources ?? []
    return raw.map((source) => ({
      source: source.source,
      cpc: source.cpc ?? null,
      cpa: source.cpa ?? null,
      invoiceCpc: source.invoice_cpc ?? null,
      invoiceCpa: source.invoice_cpa ?? null,
      type: source.type ?? (source.cpa ? 'CPA' : 'CPC'),
    }))
  })

  const baseTrafficSources = computed<CreatorTrafficSourceCost[]>(() =>
    (advertiserStore.advertiser?.trafficSourcesCosts ?? []).map(normalizeTrafficSourceCost),
  )

  const oneTimeEarnings = computed<CreatorOneTimeEarning[]>(
    () => legacyStore.getters['influencer/influencer']?.oneTimeEarnings ?? [],
  )

  const isLoading = computed<boolean>(() => Boolean(legacyStore.getters['influencer/loading']))

  const shopId = computed<number | undefined>(() => advertiserStore.advertiser?.id)

  const currencyCode = computed<string>(
    () =>
      advertiserStore.advertiser?.currencyObj?.code ??
      advertiserStore.advertiser?.currencyIsoCode ??
      '',
  )

  const currencySymbol = computed<string>(
    () => advertiserStore.advertiser?.currencyObj?.symbol ?? '',
  )

  const toLegacyTrafficSources = (
    costs: CreatorTrafficSourceCost[],
  ): CreatorTrafficSourceCostRaw[] => {
    const existing: CreatorTrafficSourceCostRaw[] =
      legacyStore.getters['influencer/influencer']?.trafficSources ?? []
    return costs.map((cost) => {
      const previous = existing.find((source) => source.source === cost.source)
      return {
        ...previous,
        source: cost.source,
        cpc: cost.cpc,
        cpa: cost.cpa,
        type: cost.type,
      }
    })
  }

  const saveCosts = async (
    creatorId: number,
    costs: CreatorTrafficSourceCost[],
  ): Promise<boolean> => {
    if (!shopId.value) {
      return false
    }
    isSaving.value = true
    error.value = null
    try {
      const payload = serializeCreatorCosts(costs, currencyCode.value)
      await request.post(`/stores/${shopId.value}/user/${creatorId}/offer`, payload)
      await legacyStore.dispatch(
        'influencer/setTrafficSourcesOnInfluencer',
        toLegacyTrafficSources(costs),
      )
      return true
    } catch (err) {
      error.value = err as Error
      return false
    } finally {
      isSaving.value = false
    }
  }

  const addOneTimePayment = async (
    creatorId: number,
    form: CreatorOneTimePaymentForm,
  ): Promise<boolean> => {
    if (!shopId.value) {
      return false
    }
    isSaving.value = true
    error.value = null
    try {
      const payload = serializeOneTimePayment(form)
      await request.post(`/stores/${shopId.value}/user/${creatorId}/oneTimePayment`, payload)
      const earning = normalizeCreatorOneTimeEarning({
        user_earnings: payload.user_earnings,
        payment_date: payload.payment_date,
        comment: payload.comment,
      } satisfies CreatorOneTimeEarningRaw)
      await legacyStore.dispatch('influencer/addOneTimeEarning', earning)
      return true
    } catch (err) {
      error.value = err as Error
      return false
    } finally {
      isSaving.value = false
    }
  }

  return {
    trafficSources,
    baseTrafficSources,
    oneTimeEarnings,
    isLoading,
    isSaving,
    error,
    currencyCode,
    currencySymbol,
    saveCosts,
    addOneTimePayment,
  }
}
